import React, { useState } from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    Paper,
    Stack,
    Tab,
    Tabs,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import {
    updateServerSettings,
    downloadBackup,
    restoreBackup,
    createBackup,
    wipeServer,
    jobServer,
} from "../http/ServerAPI";
import {LOG_ROUTE, SERVER_PLUGINS_ROUTE, SERVERS_ROUTE} from "../utils/consts";
import BreadCrumbs from "../components/Breadcrumbs";
import ServerSettingsForm from "../components/ServerSettingsForm";
import ServerBackups from "../components/ServerBackups";
import ServerStatsChart from "../components/ServerStatsChart";
import {
    Assessment,
    Backup,
    Dns,
    ListAlt,
    PlayArrow,
    RocketLaunch,
    Settings,
    Source,
    Stop,
    TipsAndUpdates,
} from "@mui/icons-material";
import {getApiErrorMessage} from "../http/apiError";
import {useServerSettingsData} from "../hooks/useServerSettingsData";
import {useSnackbar} from "../components/snackbar/SnackbarProvider";

const TAB_ITEMS = [
    {label: "Обзор", icon: <Dns fontSize="small" />},
    {label: "Параметры", icon: <Settings fontSize="small" />},
    {label: "Мониторинг", icon: <Assessment fontSize="small" />},
    {label: "Бэкапы", icon: <Backup fontSize="small" />},
];

function TabPanel({value, index, children}) {
    if (value !== index) return null;
    return <Box sx={{pt: {xs: 2, sm: 3}}}>{children}</Box>;
}

function StatCard({label, value, helper}) {
    return (
        <Card variant="outlined" sx={{height: "100%", borderRadius: 3}}>
            <CardContent>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5" fontWeight={800} mt={0.5}>{value}</Typography>
                {helper && <Typography variant="body2" color="text.secondary" mt={0.5}>{helper}</Typography>}
            </CardContent>
        </Card>
    );
}

const ServerSettingsPage = () => {
    const snackbar = useSnackbar();
    const navigate = useNavigate();
    const {server_id} = useParams();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const {settings, server, stats, backups} = useServerSettingsData(server_id);

    const [activeTab, setActiveTab] = useState(0);
    const [dialog, setDialog] = useState({open: false, action: null});
    const [downloading, setDownloading] = useState(false);
    const [confirm, setConfirm] = useState({open: false, action: null, text: "", serverId: null});
    const [loadingButton, setLoadingButton] = useState(null);

    const hrefs = [{href: SERVERS_ROUTE, name: "Список серверов"}];

    const handleConfirm = (text, action, serverId) => {
        setConfirm({open: true, action, text, serverId});
    };

    const handleConfirmClose = () => {
        setConfirm({open: false, action: null, text: "", serverId: null});
    };

    const handleConfirmedAction = async () => {
        if (!confirm.action) return;
        setLoadingButton(confirm.action);
        handleConfirmClose();

        try {
            if (confirm.action === "wipe") {
                await wipeServer(confirm.serverId);
                snackbar.success("Вайп сервера запущен");
            } else {
                await jobServer(confirm.serverId, confirm.action);
                snackbar.success("Команда отправлена серверу");
            }
            await server.reload();
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, "Не удалось выполнить действие"));
        } finally {
            setLoadingButton(null);
        }
    };

    const handleChange = (field, value) => {
        settings.setData((prev) => ({...prev, [field]: value}));
    };

    const openDialog = (action) => setDialog({open: true, action});
    const closeDialog = () => setDialog({open: false, action: null});

    const confirmDialog = async () => {
        const action = dialog.action;
        closeDialog();

        try {
            if (action === "save") {
                await updateServerSettings(server_id, settings.data);
                await settings.reload();
                snackbar.success("Настройки сохранены");
            } else if (action === "backup") {
                await createBackup(server_id);
                await backups.reload();
                snackbar.success("Бэкап создан");
            } else if (action === "restore") {
                await restoreBackup(server_id);
                snackbar.success("Восстановление запущено");
            } else if (action === "download") {
                setDownloading(true);
                await downloadBackup(server_id, setDownloading);
            }
        } catch (e) {
            snackbar.error(getApiErrorMessage(e, "Ошибка выполнения действия"));
        } finally {
            if (action === "download") setDownloading(false);
        }
    };

    const serverData = Array.isArray(server.data) ? (server.data[0] || {}) : (server.data || {});
    const settingsData = settings.data;
    const isRunning = Number(serverData.status_id) === 3;
    const isStopped = [4, 8].includes(Number(serverData.status_id));

    if (settings.loading || !settingsData) {
        return (
            <Container maxWidth="xl">
                <Box minHeight="55vh" display="flex" alignItems="center" justifyContent="center">
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" disableGutters={isMobile}>
            <Box sx={{px: {xs: 1.5, sm: 3}, py: {xs: 1.5, sm: 3}}}>
                {!isMobile && (
                    <Box mb={2.5}>
                        <BreadCrumbs hrefs={hrefs} name="Настройки сервера" />
                    </Box>
                )}

                <Stack
                    direction={{xs: "column", sm: "row"}}
                    justifyContent="space-between"
                    alignItems={{xs: "flex-start", sm: "center"}}
                    spacing={1.5}
                    mb={2}
                >
                    <Box>
                        <Typography variant={isMobile ? "h5" : "h4"} fontWeight={900}>
                            {serverData.title || settingsData.title || "Настройки сервера"}
                        </Typography>
                        <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                size="small"
                                color={isRunning ? "success" : isStopped ? "default" : "warning"}
                                label={serverData.status || serverData.server_status || "Статус неизвестен"}
                            />
                            {serverData.build_id && <Chip size="small" variant="outlined" label={`Rust build ${serverData.build_id}`} />}
                            {serverData.umod_version && <Chip size="small" variant="outlined" label={`Oxide ${serverData.umod_version}`} />}
                        </Stack>
                    </Box>
                </Stack>

                <Paper variant="outlined" sx={{borderRadius: 3, overflow: "hidden"}}>
                    <Tabs
                        value={activeTab}
                        onChange={(_, nextValue) => setActiveTab(nextValue)}
                        variant={isMobile ? "scrollable" : "fullWidth"}
                        scrollButtons={isMobile ? "auto" : false}
                        allowScrollButtonsMobile
                        sx={{
                            px: {xs: 0.5, sm: 1},
                            borderBottom: 1,
                            borderColor: "divider",
                            "& .MuiTab-root": {minHeight: 64, textTransform: "none", fontWeight: 700},
                        }}
                    >
                        {TAB_ITEMS.map((item) => (
                            <Tab key={item.label} icon={item.icon} iconPosition="start" label={item.label} />
                        ))}
                    </Tabs>
                </Paper>

                <TabPanel value={activeTab} index={0}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="Текущий онлайн" value={serverData.players_online ?? serverData.online ?? 0} helper="игроков сейчас" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="CPU" value={`${Number(serverData.cpu || 0).toFixed(2)}%`} helper="текущая загрузка" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="Память" value={`${Number(serverData.mem || 0).toFixed(2)} ГБ`} helper="используется сервером" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatCard label="Температура CPU" value={`${Number(serverData.cpu_temperature || 0).toFixed(1)} °C`} helper={`SSD: ${Number(serverData.ssd_temperature || 0).toFixed(1)} °C`} />
                        </Grid>

                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{p: {xs: 2, sm: 3}, borderRadius: 3}}>
                                <Typography variant="h5" fontWeight={800}>Управление сервером</Typography>
                                <Typography color="text.secondary" mb={2.5}>
                                    Быстрые действия и переходы к основным разделам.
                                </Typography>

                                <Stack direction={{xs: "column", sm: "row"}} spacing={1.25} flexWrap="wrap" useFlexGap>
                                    {isStopped && (
                                        <Button
                                            variant="contained"
                                            color="success"
                                            startIcon={loadingButton === "start" ? <CircularProgress size={18} /> : <PlayArrow />}
                                            onClick={() => handleConfirm("Запустить сервер?", "start", serverData.server_id)}
                                            disabled={!!loadingButton}
                                        >
                                            Старт
                                        </Button>
                                    )}
                                    {isRunning && (
                                        <Button
                                            variant="contained"
                                            color="error"
                                            startIcon={loadingButton === "stop" ? <CircularProgress size={18} /> : <Stop />}
                                            onClick={() => handleConfirm("Остановить сервер?", "stop", serverData.server_id)}
                                            disabled={!!loadingButton}
                                        >
                                            Стоп
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="warning"
                                        startIcon={loadingButton === "update" ? <CircularProgress size={18} /> : <TipsAndUpdates />}
                                        onClick={() => handleConfirm("Обновить сервер?", "update", serverData.server_id)}
                                        disabled={!!loadingButton}
                                    >
                                        Обновить
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="info"
                                        startIcon={loadingButton === "wipe" ? <CircularProgress size={18} /> : <RocketLaunch />}
                                        onClick={() => handleConfirm("Выполнить вайп сервера?", "wipe", serverData.server_id)}
                                        disabled={!!loadingButton}
                                    >
                                        Вайп
                                    </Button>
                                    <Button variant="outlined" startIcon={<Source />} onClick={() => navigate(`${SERVER_PLUGINS_ROUTE}/${serverData.server_id}`)}>
                                        Плагины
                                    </Button>
                                    <Button variant="outlined" startIcon={<ListAlt />} onClick={() => navigate(`${LOG_ROUTE}/${serverData.server_id}`)}>
                                        Логи
                                    </Button>
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <ServerSettingsForm settings={settingsData} handleChange={handleChange} openDialog={openDialog} />
                </TabPanel>

                <TabPanel value={activeTab} index={2}>
                    {stats.error && <Alert severity="warning" sx={{mb: 2}}>{stats.error}</Alert>}
                    <ServerStatsChart stats={stats.data} />
                </TabPanel>

                <TabPanel value={activeTab} index={3}>
                    {backups.error && <Alert severity="warning" sx={{mb: 2}}>{backups.error}</Alert>}
                    <ServerBackups backups={backups.data || []} downloading={downloading} openDialog={openDialog} />
                </TabPanel>

                <Dialog open={dialog.open} onClose={closeDialog} fullWidth maxWidth="xs">
                    <DialogTitle>Подтвердите действие</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Вы уверены, что хотите {dialog.action === "save" ? "сохранить изменения" : dialog.action === "backup" ? "создать бэкап" : dialog.action === "restore" ? "восстановить сервер" : "скачать бэкап"}?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeDialog}>Отмена</Button>
                        <Button onClick={confirmDialog} autoFocus>Подтвердить</Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={confirm.open} onClose={handleConfirmClose} fullWidth maxWidth="xs">
                    <DialogTitle>Подтверждение действия</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{confirm.text}</DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleConfirmClose}>Отмена</Button>
                        <Button onClick={handleConfirmedAction} color="primary" autoFocus>Подтвердить</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default ServerSettingsPage;
